import type { IConfiguration } from 'shared';

/**
 * Resolved MSAL configuration (subset of {@link IConfiguration}) used to
 * construct the underlying `PublicClientApplication`.
 */
export interface RuntimeMsalConfig {
  clientId: string;
  authority: string;
  knownAuthorities: string[];
  scopes: string[];
}

/**
 * MSAL requires every authority host outside `*.microsoftonline.com` to be
 * listed in `auth.knownAuthorities`. Azure AD B2C tenants on custom domains
 * (e.g. `logint.grundfos.com`) fall in this category — without an entry MSAL
 * silently refuses to redirect there.
 */
export function extractKnownAuthorities(authority: string): string[] {
  try {
    const host = new URL(authority).hostname;
    if (host.endsWith('.microsoftonline.com')) {
      return [];
    }
    return [host];
  } catch {
    return [];
  }
}

/**
 * OIDC reserved scopes. MSAL adds these implicitly and rejects them when
 * they appear alongside resource scopes in a B2C token request.
 */
const RESERVED_SCOPES = new Set(['openid', 'profile', 'offline_access']);
export function isReservedScope(scope: string): boolean {
  return RESERVED_SCOPES.has(scope.toLowerCase());
}

/**
 * Parse a backend `IConfiguration` payload into the strongly-typed
 * `RuntimeMsalConfig` used at MSAL construction time.
 *
 * Returns `null` if required fields are missing.
 */
export function parseRuntimeMsalConfig(config: IConfiguration | null | undefined): RuntimeMsalConfig | null {
  if (!config || !config.client_id || !config.authority) {
    return null;
  }
  return {
    clientId: config.client_id,
    authority: config.authority,
    knownAuthorities: extractKnownAuthorities(config.authority),
    scopes: (config.clientScope ?? '')
      .split(' ')
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && !isReservedScope(s)),
  };
}

/**
 * Derive MSAL redirect URIs from `<base href>` in index.html so they match
 * the deployment path automatically ("/" in dev, "/service/" in prod).
 *
 * `baseURI` is parameterised purely for testability — production callers
 * pass `document.baseURI`.
 */
export function computeRedirectUris(baseURI: string): { redirectUri: string; postLogoutRedirectUri: string } {
  return {
    redirectUri: new URL('callback', baseURI).href,
    postLogoutRedirectUri: new URL('logout/callback', baseURI).href,
  };
}
